import { t } from 'i18next';

import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';
import { InternalTransactionState, InternalTransactionStateType } from 'support/types';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';

type StateDisplay = { colour: BadgeColor; translationKey: string };

const receiverStateDisplays: Partial<Record<InternalTransactionStateType, StateDisplay>> = {
  [InternalTransactionState.delivered]: {
    colour: BadgeColor.green,
    translationKey: 'connections:testingFlowStates.delivered',
  },
  [InternalTransactionState.manuallyDelivered]: {
    colour: BadgeColor.green,
    translationKey: 'connections:testingFlowStates.delivered',
  },
  [InternalTransactionState.initial]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.denormalized]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.decanonicalized]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.pendingAutoRecovery]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.deliveryPending]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.validated_for_destination]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.canonicalized]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.enriched]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.routed]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.post_processed]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.transmitting',
  },
  [InternalTransactionState.pendingPickup]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.pendingPickup',
  },
  [InternalTransactionState.deliverySimulated]: {
    colour: BadgeColor.blue,
    translationKey: 'connections:testingFlowStates.readyForDelivery',
  },
  [InternalTransactionState.pickupFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.deliverySimulationFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.deliveryFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.canonicalizationFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.enrichedFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.routingFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.postProcessingFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.decanonicalizedFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.denormalizationFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.validation_for_destination_failed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.failed',
  },
  [InternalTransactionState.alreadySent]: {
    colour: BadgeColor.gray,
    translationKey: 'connections:testingFlowStates.dropped',
  },
  [InternalTransactionState.dropped]: {
    colour: BadgeColor.gray,
    translationKey: 'connections:testingFlowStates.dropped',
  },
  [InternalTransactionState.superseded]: {
    colour: BadgeColor.gray,
    translationKey: 'connections:testingFlowStates.superseded',
  },
};

const senderStateDisplays: Partial<Record<InternalTransactionStateType, StateDisplay>> = {
  [InternalTransactionState.initial]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.received]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.normalized]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.canonicalized]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.enriched]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.pendingAutoRecovery]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.routed]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.validated_for_destination]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.decanonicalized]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.post_processed]: {
    colour: BadgeColor.yellow,
    translationKey: 'connections:testingFlowStates.processing',
  },
  [InternalTransactionState.receivalFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.invalidForTesting',
  },
  [InternalTransactionState.normalizationFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.invalidForTesting',
  },
  [InternalTransactionState.canonicalizationFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.invalidForTesting',
  },
  [InternalTransactionState.enrichedFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.invalidForTesting',
  },
  [InternalTransactionState.routingFailed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.invalidForTesting',
  },
  [InternalTransactionState.validation_for_destination_failed]: {
    colour: BadgeColor.red,
    translationKey: 'connections:testingFlowStates.invalidForTesting',
  },
  [InternalTransactionState.alreadySent]: {
    colour: BadgeColor.gray,
    translationKey: 'connections:testingFlowStates.dropped',
  },
  [InternalTransactionState.dropped]: {
    colour: BadgeColor.gray,
    translationKey: 'connections:testingFlowStates.dropped',
  },
  [InternalTransactionState.superseded]: {
    colour: BadgeColor.gray,
    translationKey: 'connections:testingFlowStates.superseded',
  },
  [InternalTransactionState.pendingRelease]: {
    colour: BadgeColor.green,
    translationKey: 'connections:testingFlowStates.validForTesting',
  },
};

type TestingFlowMessageStateDisplayProps = {
  state: InternalTransactionStateType | undefined;
  receiver: boolean;
};

export function TestingFlowMessageStateDisplay({ state, receiver }: TestingFlowMessageStateDisplayProps) {
  const { colour, text } = getColourAndText(state, receiver);

  return <Badge color={colour}>{text}</Badge>;
}

const getColourAndText = (
  state: TestingFlowMessageStateDisplayProps['state'],
  receiver: TestingFlowMessageStateDisplayProps['receiver'],
): { colour: BadgeColor; text: string } => {
  if (!state) {
    return { colour: BadgeColor.gray, text: '' };
  }

  const display = (receiver ? receiverStateDisplays : senderStateDisplays)[state];
  if (display) {
    return { colour: display.colour, text: t(display.translationKey) };
  }

  const perspective = receiver ? 'receiver' : 'sender';
  logError(new GenericError(`Internal state of transaction was not translated for the ${perspective}`, state));

  return { colour: BadgeColor.gray, text: String(state) };
};

/**
 * i18n keys
 * t('connections:testingFlowStates.delivered')
 * t('connections:testingFlowStates.transmitting')
 * t('connections:testingFlowStates.pendingPickup')
 * t('connections:testingFlowStates.readyForDelivery')
 * t('connections:testingFlowStates.failed')
 * t('connections:testingFlowStates.dropped')
 * t('connections:testingFlowStates.superseded')
 * t('connections:testingFlowStates.processing')
 * t('connections:testingFlowStates.invalidForTesting')
 * t('connections:testingFlowStates.validForTesting')
 */
